import { compact } from "lodash";
import { t } from "@/i18n-js/instance";
import {
  PRICE_SCENARIOS,
  isTrialThenValue,
} from "@/react/helpers/paywallPriceHelpers";
import { formatPaywallCurrencyAmount } from "@/react/helpers/paywalls/currencies";
import type { PaywallCheckoutPreview } from "@circle-react/types";
import type { PreviewComponents } from "../../../types";
import { shouldExpandFirstPayment } from "../../FirstPaymentPreview/variants/helpers";
import { Lines } from "../Lines";
import { RawLines } from "../RawLines";
import { shouldExpandFuturePayments } from "./helpers";

export const getTooltipText = (
  couponDuration: string,
  count: string | number | undefined,
  hasMore: boolean,
) => {
  const locales = compact([
    "paywall_checkout.future_payments.discount_phrase_v2",
    couponDuration,
    hasMore && couponDuration != "forever" ? "plus" : null,
  ]);

  return t(locales, { count });
};

const getDiscountCounter = (preview: PaywallCheckoutPreview) => {
  const couponApplicationCount = Number(
    preview.coupon_applications.application_count,
  );
  let discountsToRemove = 0;

  if (preview.preview_scenario === PRICE_SCENARIOS.value) {
    discountsToRemove = 2;
  }

  if (
    [PRICE_SCENARIOS.trialThenValue, PRICE_SCENARIOS.upfrontThenValue].includes(
      preview.preview_scenario,
    )
  ) {
    // We're always rendering the First payment, so we should stick to the same
    // logic we have for `value scenario`.
    discountsToRemove = 2;
  }

  if (
    [
      PRICE_SCENARIOS.trialThenUpfrontThenValue,
      PRICE_SCENARIOS.upfrontThenTrialThenValue,
    ].includes(preview.preview_scenario)
  ) {
    // In this case, we always show the Future payments, which it always be 3 coupons to be removed.
    discountsToRemove = 3;
  }

  return Math.max(couponApplicationCount - discountsToRemove, 0);
};

export const subscriptionVariant: PreviewComponents = [
  [
    Lines.HeaderLine,
    (preview: PaywallCheckoutPreview) => {
      if (isTrialThenValue(preview.preview_scenario)) {
        return {
          label: t(
            "paywall_checkout.future_payments.future_payments_after_x_trial_days",
            { count: preview?.trial_period?.days },
          ),
          value: shouldExpandFirstPayment(preview)
            ? preview.first_payment.formatted_amount_due
            : null,
        };
      }

      return {
        value: shouldExpandFuturePayments(preview)
          ? preview?.future_payments?.formatted_amount_due
          : null,
      };
    },
  ],
  [
    RawLines.RegularPriceLine,
    (preview: PaywallCheckoutPreview) => {
      if (isTrialThenValue(preview.preview_scenario)) {
        return {
          hide: !shouldExpandFirstPayment(preview),
          value: preview.first_payment.formatted_subtotal,
        };
      }

      return {
        hide: !shouldExpandFuturePayments(preview),
        value: preview.future_payments?.formatted_subtotal,
      };
    },
  ],
  [
    RawLines.DiscountLine,
    (preview: PaywallCheckoutPreview) => {
      const couponDuration = preview?.paywall_coupon?.duration;
      const couponApplicationCount = getDiscountCounter(preview);
      const hasMore = preview.coupon_applications.has_more;

      if (isTrialThenValue(preview.preview_scenario)) {
        return {
          hide: preview.first_payment.discount === 0,
          value: preview.first_payment.formatted_discount,
          tooltip:
            couponDuration &&
            couponApplicationCount > 0 &&
            getTooltipText(couponDuration, couponApplicationCount, hasMore),
        };
      }

      return {
        hide: (preview?.future_payments?.discount || 0) === 0,
        tooltip:
          couponDuration &&
          couponApplicationCount > 0 &&
          getTooltipText(couponDuration, couponApplicationCount, hasMore),
      };
    },
  ],
  [
    RawLines.TaxLine,
    (preview: PaywallCheckoutPreview) => {
      if (isTrialThenValue(preview.preview_scenario)) {
        return {
          hide: preview.first_payment.tax === 0,
          value: preview.first_payment.formatted_tax,
        };
      }

      return {
        hide: (preview?.future_payments?.tax || 0) === 0,
        value: preview?.future_payments?.formatted_tax,
      };
    },
  ],
  [
    RawLines.SubtotalLine,
    (preview: PaywallCheckoutPreview) => {
      if (isTrialThenValue(preview.preview_scenario)) {
        const paywallPrice = preview.paywall_price;
        let dateString = "";

        let value = {};
        if (preview?.paywall_coupon?.duration === "forever") {
          dateString = preview?.future_payments?.formatted_start_on || "";
          value = {
            value: formatPaywallCurrencyAmount(
              preview.currency,
              preview.future_payments?.amount_due ||
                preview.paywall_price.amount,
            ),
            valueWeight: "normal",
          };
        } else if (preview?.paywall_coupon?.duration === "once") {
          dateString = preview?.future_payments?.formatted_start_on || "";
          value = {
            value: formatPaywallCurrencyAmount(
              preview.currency,
              preview.paywall_price.amount,
            ),
            valueWeight: "normal",
          };
        } else {
          dateString = preview.first_payment.formatted_due_on;
          value = {
            value: preview?.first_payment?.formatted_amount_due,
            valueWeight: shouldExpandFirstPayment(preview)
              ? "normal"
              : "semibold",
          };
        }

        return {
          label: t(
            [
              "paywall_checkout.future_payments.payments_starting_on_phrase",
              paywallPrice.price_type,
              paywallPrice.interval_type,
            ],
            {
              date: dateString,
            },
          ),
          ...value,
        };
      }

      return {
        value: preview?.future_payments?.formatted_amount_due,
        valueWeight: shouldExpandFuturePayments(preview)
          ? "normal"
          : "semibold",
      };
    },
  ],
];
